import type {QueryClient, QueryKey} from '@github-ui/react-query'

const queryAnnouncementClaims = new WeakMap<QueryClient, WeakMap<object, Set<string>>>()

/**
 * The merge box can render more than once on a page (main column and side panel), and those copies
 * share a query cache. Without coordination each copy would announce the same transition, so the
 * first caller to claim a given `(query, transition, update count)` tuple wins and the rest stay
 * silent.
 *
 * Callers wrap this in their own `useCallback` so the dependency list can name the primitives the
 * query key is built from.
 */
export function claimQueryAnnouncement(queryClient: QueryClient, queryKey: QueryKey, transition: string): boolean {
  const sharedQuery = queryClient.getQueryCache().find({queryKey, exact: true})
  if (!sharedQuery) return false

  let clientClaims = queryAnnouncementClaims.get(queryClient)
  if (!clientClaims) {
    clientClaims = new WeakMap()
    queryAnnouncementClaims.set(queryClient, clientClaims)
  }

  let claims = clientClaims.get(sharedQuery)
  if (!claims) {
    claims = new Set()
    clientClaims.set(sharedQuery, claims)
  }

  const {dataUpdateCount, errorUpdateCount} = sharedQuery.state
  const transitionId = `${transition}:${dataUpdateCount}:${errorUpdateCount}`
  if (claims.has(transitionId)) return false

  claims.add(transitionId)
  return true
}
